import {
  type Atom,
  type RunResult,
  type RuntimeContext,
  coreAtoms,
  AgentError,
  isProcedureToken,
  resolveProcedureToken,
  membraneValueFrom,
  recordVmEvent,
  newScopeState,
  admitGuestSchema,
  VALIDATION_FUEL_PER_STEP,
  type Capabilities,
} from './runtime'
import { TypedBuilder, type BaseNode, type BuilderType } from '../builder'
import { validate, isBuilder } from 'tosijs-schema'
import {
  checkAstVersion,
  astVersionOf,
  AST_VERSION_LEGACY,
} from './ast-version'
import { getTranspiler } from './transpiler-slot'
import {
  admitRunOptions,
  budgetOption,
  type RunOptions,
  sourceBytesOver,
  timerMs,
  DEFAULT_MAX_SOURCE_BYTES,
} from './admission'

/**
 * The transpiler, INJECTED rather than imported.
 *
 * `run()` accepts AJS source as well as an AST, and resolving that string used to mean a
 * static `import { transpile } from '../lang/core'` — which put the entire transpiler, acorn
 * included, inside `tjs-lang/vm`. Two costs:
 *
 *   - **Size.** 221.5 KB against 56.3 KB for the same `AgentVM` without it (minified,
 *     `tosijs-schema` external). 75% of the VM bundle was a parser.
 *   - **Attack surface.** A sandbox that parses is a sandbox whose parser is reachable from
 *     untrusted input, upstream of fuel, timeouts, capabilities and the membrane — the exact
 *     position the `test`-block leak occupied (0.13.10,
 *     `eval-no-transpile-execution.test.ts`). That leak was closed; the shape that permitted
 *     it should not survive it.
 *
 * So the dependency is now supplied by the ENTRY POINT (`./transpiler-slot`, internal). `tjs-lang/vm` calls
 * `setTranspiler(transpile)` and behaves exactly as before; `tjs-lang/vm-ast` does not, and
 * is therefore a VM that cannot parse because it contains no parser.
 *
 * **What this guarantees, stated precisely:** *no parser is present in this bundle* — not
 * *this object refuses source even when a parser is loaded*. The binding is module-level, so
 * importing both entries into one bundle shares it. That is the guarantee worth having: a
 * consumer who imported `tjs-lang/vm` already has the parser, so a per-instance refusal would
 * protect nothing it does not already have. If a per-instance guard is ever wanted, a
 * constructor option is the shape — do not quietly reinterpret this one.
 */
/**
 * Floor for the run-level default timeout. The actual default is derived from
 * the registered atoms (slowest atom × 2 — see `defaultRunTimeout`), but never
 * drops below this for a VM whose atoms are all fast.
 */
const MIN_DEFAULT_RUN_TIMEOUT_MS = 60_000

/**
 * Argument bytes one unit of fuel pays for — the rate binding the same data costs
 * (`ARRAY_FUEL_PER_ELEMENT`/`HEAP_WALK_FUEL_PER_NODE` = 0.001 fuel per ~8-byte node). The
 * run's fuel therefore bounds how much argument data it may be handed: admission is work,
 * and no work in a run is unbudgeted.
 */
export const ARG_BYTES_PER_FUEL = 8000

/** The source-parsing path's deprecation is noted once per process. */
let deprecationNoted = false

/**
 * Ceiling on run arguments whatever the fuel — the same 4MB the capability direction takes.
 *
 * It bounds pre-budget work ABSOLUTELY: the admission walk measured ~45ns/byte at its worst
 * (dense numeric arrays — a property descriptor per element, then `Object.keys` to find
 * non-index properties), so 4MB is ~180ms however much fuel the caller brings. It was 64MB,
 * and at fuel 10000 a 10MB argument cost ~2.4s before anything stopped it (0.14.0 final
 * re-review 3, B-2). A host that needs more raises `argsMaxBytes` knowingly. The structural
 * fix — a membrane that copies while it walks, so it never enumerates what it will not copy
 * — is tracked in TODO.md.
 */
export const DEFAULT_ARGS_MAX_BYTES = 4 * 1024 * 1024

export type { RunOptions } from './admission'

/** Validation of an AST's inputSchema visits at most this many argument paths. */
const MAX_VALIDATION_PATHS = 1_000_000
/**
 * ...and does at most this much work, counted as (schema nodes) × (argument paths). Validation
 * runs before the run's timer exists, so with `fuel: Infinity` a path cap alone let a 10k-node
 * schema validate 125k leaves synchronously for 8s (seventeenth re-review m2) — about 6ns per
 * step, so this ceiling is ~0.6s of worst-case work.
 */
const MAX_VALIDATION_STEPS = 100_000_000

/**
 * Nodes in data as VALIDATION visits them: per occurrence, so a shared object is counted at
 * every path that reaches it (a WeakSet-deduped count let DAG-shaped arguments validate for
 * 1.7s on 0.25 fuel — fifteenth re-review M3). Stops at `limit`: past it the answer is only
 * "more than the run can afford", and counting further would be the unbounded work itself.
 */
function countPaths(value: unknown, limit: number): number {
  let n = 0
  const stack = [value]
  while (stack.length) {
    const v = stack.pop()
    if (++n > limit) return n
    if (v && typeof v === 'object')
      for (const x of Object.values(v)) stack.push(x)
  }
  return n
}

export class AgentVM<M extends Record<string, Atom<any, any>>> {
  readonly atoms: typeof coreAtoms & M

  private _defaultRunTimeout?: number

  constructor(customAtoms: M = {} as M) {
    this.atoms = { ...coreAtoms, ...customAtoms }
    // Every static atom timeout is checked where the VM is BUILT. `defineAtom` checks its own,
    // but an atom object can be written by hand; checking here means a bad one fails at
    // construction — a configuration error, reported where it was made — rather than as a
    // throw out of `vm.run` (re-review 13, F-2).
    for (const atom of Object.values(this.atoms)) {
      const raw = (atom as any).timeoutMs
      if (typeof raw !== 'function')
        budgetOption(`timeoutMs of atom '${atom.op}'`, raw, 1000)
    }
  }

  /**
   * Default run-level wall-clock timeout when `run()` is given no explicit
   * `timeoutMs`. Derived as `max(per-atom timeoutMs) × 2` over the registered
   * atoms (with headroom for an agent that chains a couple of slow calls), so
   * the run-level backstop can never be shorter than the slowest single atom's
   * own budget — otherwise that per-atom budget would be dead config (e.g.
   * `llmVision`/`llmPredictBattery` are 120s; a fixed 60s run default would kill
   * them mid-call). Atoms with `timeoutMs: 0` (no timeout, e.g. `seq`) are
   * excluded; the result is floored at {@link MIN_DEFAULT_RUN_TIMEOUT_MS}.
   * Self-adjusting: registering a slower custom atom raises the default.
   */
  get defaultRunTimeout(): number {
    if (this._defaultRunTimeout === undefined) {
      let slowest = 0
      for (const atom of Object.values(this.atoms)) {
        // undefined timeoutMs means the per-atom default (1000ms); 0 means none. A function
        // timeout exists only per call, and Infinity — like 0 — means "no per-atom limit",
        // which says nothing about how long a RUN should get: counting it made every run on
        // this VM unbounded. Validity was established by the constructor.
        const raw = (atom as any).timeoutMs
        if (typeof raw === 'function') continue
        const t: number = raw ?? 1000
        if (t > 0 && Number.isFinite(t) && t > slowest) slowest = t
      }
      this._defaultRunTimeout = Math.max(
        MIN_DEFAULT_RUN_TIMEOUT_MS,
        slowest * 2
      )
    }
    return this._defaultRunTimeout
  }

  get builder(): BuilderType<typeof coreAtoms & M> {
    return new TypedBuilder(this.atoms) as any
  }

  // Typed helper for builder
  get Agent(): BuilderType<typeof coreAtoms & M> {
    return new TypedBuilder(this.atoms) as any
  }

  /** @deprecated Use `Agent` instead */
  get A99(): BuilderType<typeof coreAtoms & M> {
    return this.Agent
  }

  resolve(op: string) {
    // OWN atoms only: `op` comes from the AST, and `this.atoms` is a plain object, so a step
    // with `op: 'toString'` (or `'__defineGetter__'`) found Object.prototype's method and failed
    // as "atom.exec is not a function" instead of "Unknown Atom".
    return Object.prototype.hasOwnProperty.call(this.atoms, op)
      ? this.atoms[op]
      : undefined
  }

  getTools(filter: 'flow' | 'all' | string[] = 'all') {
    let targetAtoms = Object.values(this.atoms)

    if (Array.isArray(filter)) {
      targetAtoms = targetAtoms.filter((a) => filter.includes(a.op))
    } else if (filter === 'flow') {
      const flowOps = [
        'seq',
        'if',
        'while',
        'return',
        'try',
        'varSet',
        'varGet',
        'scope',
      ]
      targetAtoms = targetAtoms.filter((a) => flowOps.includes(a.op))
    }

    return targetAtoms.map((atom) => ({
      type: 'function',
      function: {
        name: atom.op,
        description: atom.docs,
        parameters: isBuilder(atom.inputSchema)
          ? atom.inputSchema.schema
          : atom.inputSchema ?? {},
      },
    }))
  }

  /**
   * Run an agent: an AST, or a stored-procedure token.
   *
   * **Passing AJS SOURCE here is deprecated (0.14.0).** It still works, but the VM should never
   * be the thing that parses: parsing is the one step whose worst case is caller-controlled
   * and happens before any budget. Transpile separately — `transpile` from `tjs-lang/lang`, in
   * a worker, another process, or on the caller's machine — and run the AST with
   * `tjs-lang/vm-ast`, which contains no parser, so a bad payload can take down only the step
   * that parsed it. Source is capped at 8KB (`maxSourceBytes`) meanwhile.
   */
  async run(
    astOrToken: BaseNode | string,
    args: Record<string, any> = {},
    options: RunOptions = {}
  ): Promise<RunResult> {
    // ADMISSION, before anything is computed from the options or done with the input: every
    // budget is derived from these numbers, and `fuel: 'abc'` made the argument budget NaN
    // (0.14.0 final re-review 3, B-1). It ran AFTER the source-string transpile, so an
    // unbounded compile preceded even this check (re-review 4, B-1). See ./admission.ts.
    // Read ONCE into a frozen record; nothing below reads `options` again (re-review 15: a
    // getter answered the check one way and the run another).
    const admitted = admitRunOptions(options)
    if (typeof admitted === 'string') {
      const error = new AgentError(admitted, 'vm.run')
      return { result: error, error, fuelUsed: 0, warnings: undefined }
    }

    // Resolve string input to AST
    let ast: BaseNode
    if (typeof astOrToken === 'string') {
      if (isProcedureToken(astOrToken)) {
        // Procedure token - lookup stored AST
        ast = resolveProcedureToken(astOrToken) as BaseNode
      } else {
        // AJS source code - transpile to AST
        const transpileImpl = getTranspiler()
        if (!transpileImpl)
          throw new Error(
            `This VM accepts an AST, not source: no transpiler is wired in. ` +
              `That is the point of the 'tjs-lang/vm-ast' build — it contains no parser, so ` +
              `no parser defect is reachable through it. Transpile on the CALLER's side, ` +
              `where the source is your own, and send the AST:\n\n` +
              `    import { transpile } from 'tjs-lang/lang'\n` +
              `    const { ast } = transpile(source)\n` +
              `    await vm.run(ast, args)\n\n` +
              `If you want the VM to parse for you, import 'tjs-lang/vm' instead.`
          )
        // Capped like every other source entry (Eval, SafeFunction, runCode): transpilation
        // runs before fuel or timeout, and `tjs-lang/vm` documents that it accepts source.
        const maxSource = admitted.maxSourceBytes ?? DEFAULT_MAX_SOURCE_BYTES
        const over = sourceBytesOver(astOrToken, maxSource)
        if (over !== null) {
          const error = new AgentError(
            `Source is ${over} bytes, over the ${maxSource}-byte limit. Transpilation runs ` +
              `BEFORE fuel and timeout apply, so oversized source is refused rather than ` +
              `metered. Raise maxSourceBytes if the source is trusted.`,
            'vm.run'
          )
          return { result: error, error, fuelUsed: 0, warnings: undefined }
        }
        // DEPRECATED path (0.14.0): the VM should never be the thing that parses. Parsing is
        // the one step whose worst case is caller-controlled and pre-budget, so a host serving
        // untrusted callers transpiles SEPARATELY (a worker, a separate process, the caller's
        // machine) and hands the AST to `tjs-lang/vm-ast`, which has no parser at all — a bad
        // payload can then take down only the step that parsed it. Still works; noted once per
        // process in the flight recorder (never on the console, never changing behaviour).
        // Counted as noted only once something actually RECORDED it — with no recorder
        // installed yet, the first call would otherwise spend it (re-review 10).
        if (!deprecationNoted)
          deprecationNoted = recordVmEvent({
            source: 'vm',
            severity: 'notice',
            message:
              'vm.run(source) is deprecated. Run ASTs with tjs-lang/vm-ast, and transpile ' +
              "OUTSIDE the VM host: on the caller's machine, or in a worker or separate process " +
              'you can afford to lose. A hostile payload then costs only the step that parsed it. ' +
              'If you must transpile untrusted source in-process, cap it: ' +
              'transpile(source, { maxSourceBytes: 8192 }) from tjs-lang/lang.',
          })
        try {
          // checked against THIS VM's atoms (twenty-third re-review)
          ast = transpileImpl(astOrToken, { atoms: this.atoms as any })
            .ast as BaseNode
        } catch (e: any) {
          throw new Error(`AJS transpilation failed: ${e.message}`, {
            cause: e,
          })
        }
      }
    } else {
      ast = astOrToken
    }

    const startFuel = admitted.fuel ?? 1000

    // Run-level wall-clock timeout. Agents are typically IO-bound; the default
    // is derived from the registered atoms (slowest × 2) so it always covers the
    // slowest atom's own budget. See `defaultRunTimeout`.
    const timeoutMs = admitted.timeoutMs ?? this.defaultRunTimeout

    // The host's capabilities object, EXACTLY as passed: never copied, wrapped or written. Three
    // rounds tried (a flat copy lost prototype members; a shadow lost the receiver, so `#private`
    // fields threw; the original wrote the default store INTO the host object, sharing it across
    // runs) — rc.2 pre-tag reviews. The VM only needed somewhere to put a default store, and that
    // belongs to the RUN: `defaultStore` below, read by `storeOf(ctx)` when the host has none.
    const hostCaps = admitted.capabilities
    if (
      hostCaps !== undefined &&
      (hostCaps === null || typeof hostCaps !== 'object')
    )
      throw new Error('vm.run: capabilities must be an object')
    const capabilities: Capabilities = hostCaps ?? {}

    // Track warnings
    const warnings: string[] = []

    // The run's default in-memory store (with a warning), used only when the host gives none
    const memoryStore = new Map<string, any>()
    let warned = false
    const warn = () => {
      if (!warned) {
        warned = true
        warnings.push(
          'Using default in-memory store (not suitable for production)'
        )
      }
    }
    const defaultStore: NonNullable<Capabilities['store']> = {
      get: async (key) => {
        warn()
        return memoryStore.get(key)
      },
      set: async (key, value) => {
        warn()
        memoryStore.set(key, value)
      },
    }

    // REJECT BEFORE ACQUIRING ANYTHING.
    //
    // Both of these used to sit after the timer and the caller's abort listener were
    // created but before the `try` — so neither exit cleared the timer nor aborted the
    // controller, while the comment below claimed the `finally` "guarantees on every exit
    // path". Measured: five input-validation failures left five live timers, five root-op
    // throws left five more, and five ordinary runs left none. A pending timer also keeps
    // the event loop alive, so a host that validates a batch of agents and exits does not,
    // for up to `timeoutMs` (default `fuel × 10ms`) after the last rejection.
    //
    // Moving them up is better than widening the `try`: an argument that never runs should
    // not allocate a timer and a listener only to release them. Nothing is held here yet,
    // so nothing can leak.
    // Refuse a format this build cannot read, BEFORE inspecting its shape.
    //
    // Checked first because a future AST may legitimately have a different root: judging it by
    // today's rules would report "must be 'seq'" for something that is simply newer. And a
    // version field nobody acts on is decoration — running an AST whose format we do not
    // understand means guessing at the meaning of untrusted code, which is the one thing a
    // sandbox must not do. See src/vm/ast-version.ts.
    checkAstVersion(ast, 'AgentVM.run')

    if (ast.op !== 'seq')
      throw new Error(
        "Root AST must be 'seq'. Ensure you're passing a transpiled agent (use ajs`...` or transpile())."
      )

    // ARGUMENTS cross the same membrane as capability returns: they are host values entering
    // guest state. Without it a class instance passed as an argument arrived LIVE, and the
    // `methodCall` allowlist filters method NAMES, not owners — so `svc.slice(0)` on a host
    // object whose class defines `slice` ran host code (reached through `Eval`'s context,
    // 0.14.0). The copy keeps the data and drops the prototype, so the methods stay behind;
    // an own function or getter is rejected. Checked before the schema, which should see
    // what the guest will see.
    //
    // ADMISSION IS BUDGETED. The walk is proportional to the caller's data and runs before
    // any atom, so an uncapped one was pre-fuel work the caller controls: a 10MB request body
    // cost ~2.4s of CPU before "Out of Fuel" (0.14.0 final re-review 2, B-1 — the third block
    // of this class in one cycle, after Eval's unmeasured keys and its scan before the gate).
    // The budget is what the run's own fuel would pay to bind the same data, capped by
    // `argsMaxBytes`; the walk stops the moment it is exceeded, so a refusal is cheap. What
    // crosses is then CHARGED, at the rate binding it costs, so admission is metered like
    // everything else.
    const argsCap = admitted.argsMaxBytes ?? DEFAULT_ARGS_MAX_BYTES
    const fuelBytes = startFuel * ARG_BYTES_PER_FUEL
    // NOT bounded by the heap ceiling, deliberately: the arguments are admitted before the run
    // exists, capped by `argsMaxBytes` and by the fuel, and the ceiling applies when they are bound
    // (cumulative review 15, m2: documented in vm-budgets.md as I1's argument exception).
    const argsBudget = Math.min(argsCap, fuelBytes)
    const crossed = membraneValueFrom('run argument', args, argsBudget)
    if (!crossed.ok) {
      // When the FUEL was the binding limit, the run could not pay to admit its arguments:
      // that is fuel exhaustion, and hosts detect it by that message.
      const outOfFuel =
        fuelBytes < argsCap && /-byte membrane budget/.test(crossed.reason)
      const error = new AgentError(
        outOfFuel
          ? 'Out of Fuel'
          : `Capability boundary rejected the run arguments: ${crossed.reason}`,
        'vm.run'
      )
      return {
        result: error,
        error,
        // A refusal still did the walk up to the budget: charge it, so a host's accounting
        // sees the work (it logged 0 for a cap-bound refusal).
        fuelUsed: outOfFuel
          ? startFuel
          : Math.min(startFuel, argsBudget / ARG_BYTES_PER_FUEL),
        trace: admitted.trace ? [] : undefined,
        warnings: warnings.length > 0 ? warnings : undefined,
      }
    }
    const admissionFuel = (crossed.bytes ?? 0) / ARG_BYTES_PER_FUEL
    // a null prototype: an argument read can find only what the caller passed (sixteenth
    // re-review B1 — `args.constructor` was the host's `Object`)
    args = Object.assign(
      Object.create(null),
      crossed.value as Record<string, any>
    ) as Record<string, any>

    const inputSchema = (ast as any).inputSchema
    // The AST's own schema is GUEST data: admitted before tosijs-schema compiles anything in it,
    // and before any fuel or timeout exists (rc.2 twelfth re-review B1).
    let refused: AgentError | undefined
    // Validation is (schema nodes) × (argument nodes) of work, done before the run's fuel
    // counter exists: it is added to the admission fuel the run starts by paying (fourteenth
    // re-review M3). Argument nodes are counted on the copy that crossed, per occurrence (as
    // validation visits them): a 2MB string is one node, a shared object is counted per path.
    let validationFuel = 0
    if (inputSchema)
      try {
        const nodes = admitGuestSchema(inputSchema, 'vm.run')
        // count only as far as the run could pay for — and never past an absolute cap: with
        // `fuel: Infinity` the bound was infinite, and cyclic arguments hung admission
        // (sixteenth re-review B3). Past the cap the arguments are too large to validate.
        const ceiling = Math.min(
          MAX_VALIDATION_PATHS,
          Math.floor(MAX_VALIDATION_STEPS / Math.max(1, nodes))
        )
        const affordable = Math.min(
          ceiling,
          Math.ceil(
            Math.max(0, startFuel - admissionFuel) /
              (nodes * VALIDATION_FUEL_PER_STEP)
          )
        )
        const paths = countPaths(args, affordable)
        if (paths > ceiling)
          throw new AgentError(
            `Arguments too large to validate against the inputSchema (over ${ceiling} paths for a ${nodes}-node schema): validate in the host, or pass fewer, smaller arguments`,
            'vm.run'
          )
        validationFuel = nodes * paths * VALIDATION_FUEL_PER_STEP
      } catch (e) {
        if (!(e instanceof AgentError)) throw e
        refused = e
      }
    // paid BEFORE the validation runs: a run that cannot afford it is refused without it
    if (!refused && admissionFuel + validationFuel >= startFuel)
      refused = new AgentError('Out of Fuel', 'vm.run')
    if (inputSchema && (refused || !validate(args, inputSchema))) {
      const error =
        refused ??
        new AgentError(
          `Input validation failed: args do not match expected schema`,
          'vm.run'
        )
      return {
        result: error,
        error,
        fuelUsed: 0,
        // No step ran, so the trace is empty rather than absent when tracing is on —
        // the same value `ctx.trace` carried at this point, which is now created below.
        trace: admitted.trace ? [] : undefined,
        warnings: warnings.length > 0 ? warnings : undefined,
      }
    }

    // Create abort controller for timeout enforcement
    const controller = new AbortController()
    // A RUN timeout of 0 means the deadline has already passed — it always did, and a host
    // passing its remaining deadline passes 0 once it is spent. Reading it as "no timer" (as
    // a per-atom override's 0 is documented to mean) made a spent deadline an unlimited run
    // (0.14.0 final re-review 5, M-1). Infinity is no timer; `setTimeout` turned it into 1ms.
    const armed = timeoutMs === 0 ? 0 : timerMs(timeoutMs)
    /** Whether OUR deadline (not the caller's signal) ended the run — for the message. */
    let timedOut = false
    const timeout =
      armed === undefined
        ? undefined
        : setTimeout(() => {
            timedOut = true
            controller.abort()
          }, armed)
    // A spent deadline is spent NOW. A zero-delay timer only fires after the microtasks a
    // run is made of, so a compute-only agent completed anyway (0.14.0 final re-review 6).
    if (armed === 0) {
      timedOut = true
      controller.abort()
    }

    // Link external signal if provided.
    //
    // `{ signal: controller.signal }` is the removal mechanism, not a second abort source:
    // it tells the caller's signal to drop this listener as soon as OUR controller aborts,
    // which the `finally` below now guarantees on every exit path. Without it the listener
    // accumulated on a long-lived caller signal for the life of the process — measured at
    // ~2.1KB per run, 41.6MB retained after 20,000 runs against one shared signal (vs
    // 1.59MB with no signal at all). A host that runs many short agents under one
    // cancellation scope is the normal case, not an exotic one.
    if (admitted.signal) {
      admitted.signal.addEventListener('abort', () => controller.abort(), {
        signal: controller.signal,
      })
      // An 'abort' listener never fires for a signal that is ALREADY aborted, so a cancelled
      // caller's run went ahead, capabilities and all (0.14.0 final re-review 7, M-2) — the
      // sibling of the spent-deadline case above.
      if (admitted.signal.aborted) controller.abort()
    }

    // The run's heap roots: its scope, its memo cache and its arguments. Every scope created
    // during the run joins this set (`newScopeState`) and leaves it when released.
    const memo = new Map()
    const heapRoots = new Set<object>([memo])
    if (args && typeof args === 'object') heapRoots.add(args)
    const ctx: RuntimeContext = {
      astVersion: astVersionOf(ast) ?? AST_VERSION_LEGACY,
      // The run's memoize cache, created HERE rather than lazily by the first `memoize` to run:
      // a cache created inside a child scope (a v2 \`while\` iteration) was a property of that
      // scope's context and died with it, so a memoized call in a loop re-ran — and re-billed
      // fuel and quota — every pass (rc.2 review B3). Child scopes share it by reference.
      memo,
      fuel: { current: startFuel - admissionFuel - validationFuel },
      args,
      state: newScopeState({ heapRoots }),
      heapRoots,
      consts: new Set(),
      // Created WITH the run, never lazily: see `heapAccount` in runtime.ts.
      heapAccount: { bytes: 0, transient: 0 },
      // Created WITH the run: a refusal at the capability boundary ends it (see runtime.ts).
      halt: {},
      heapPerKey: new Map(),
      capabilities,
      defaultStore,
      resolver: (op) => this.resolve(op),
      output: undefined,
      signal: controller.signal,
      costOverrides: admitted.costOverrides,
      quotas: admitted.quotas,
      maxSourceBytes: admitted.maxSourceBytes,
      quotaUsed: admitted.quotaUsed ?? {},
      quotaLocal: Object.create(null),
      timeoutOverrides: admitted.timeoutOverrides,
      context: admitted.context,
      membraneMaxBytes: admitted.membraneMaxBytes,
      maxHeapBytes: admitted.maxHeapBytes,
      warnings, // Shared warnings array
      helpers: (ast as any).helpers, // Local helper bodies, called by name via callLocal
    }

    if (admitted.trace) {
      ctx.trace = []
    }

    try {
      // Race execution against timeout
      await Promise.race([
        this.resolve('seq')?.exec(ast, ctx),
        new Promise<never>((_, reject) => {
          controller.signal.addEventListener('abort', () => {
            reject(
              new Error(
                `Execution timeout after ${timeoutMs}ms. Pass a higher \`timeoutMs\` to vm.run() or set per-atom \`timeoutOverrides\` for slow IO atoms.`
              )
            )
          })
          // If already aborted, reject immediately
          if (controller.signal.aborted) {
            reject(
              new Error(
                `Execution timeout after ${timeoutMs}ms. Pass a higher \`timeoutMs\` to vm.run() or set per-atom \`timeoutOverrides\` for slow IO atoms.`
              )
            )
          }
        }),
      ])
    } catch (e: any) {
      // Convert timeout error to AgentError
      if (
        e.message?.includes('timeout') ||
        e.message?.includes('aborted') ||
        controller.signal.aborted
      ) {
        // A deadline is a timeout; any other abort is the CALLER's signal, and calling that a
        // timeout sent people looking for a timeoutMs to raise.
        ctx.error = new AgentError(
          timedOut || !admitted.signal?.aborted
            ? `Execution timeout after ${timeoutMs}ms. Pass a higher \`timeoutMs\` to vm.run() or set per-atom \`timeoutOverrides\` for slow IO atoms.`
            : 'Execution aborted by the caller (options.signal)',
          'vm.run'
        )
      } else {
        // Re-throw non-timeout errors
        throw e
      }
    } finally {
      clearTimeout(timeout)
      // A step that saw the abort reports "Execution aborted"; when the cause was the run's
      // own deadline, say so, in the words hosts already detect.
      if (
        !timedOut &&
        admitted.signal?.aborted &&
        ctx.error?.message === 'Execution aborted'
      )
        ctx.error = new AgentError(
          'Execution aborted by the caller (options.signal)',
          'vm.run'
        )
      if (timedOut && ctx.error?.message === 'Execution aborted')
        ctx.error = new AgentError(
          `Execution timeout after ${timeoutMs}ms. Pass a higher \`timeoutMs\` to vm.run() or set per-atom \`timeoutOverrides\` for slow IO atoms.`,
          'vm.run'
        )
      // The run is over — cancel anything it still has in flight.
      //
      // Previously only the TIMEOUT aborted, so a run ending any other way (fuel
      // exhaustion, an atom error, or plain success) cleared the timer and left outbound
      // requests alive with nothing left to cancel them. A time box you can only rely on
      // when it expires is not a time box.
      //
      // Signalling, not awaiting: we do not wait for capabilities to unwind. Waiting is
      // exactly how graceful shutdown becomes the vulnerability — a capability that never
      // settles would hold the run open, turning cancellation into a path that starts
      // unmetered work.
      controller.abort()
    }

    // A halted run reports what halted it, whatever a later step left behind.
    if (ctx.halt.error) {
      ctx.error = ctx.halt.error
      ctx.output = ctx.halt.error
    }

    // If there's an error but no output was set, set the error as output
    if (ctx.error && ctx.output === undefined) {
      ctx.output = ctx.error
    }

    // Merge any warnings added via console.warn
    const allWarnings = [...warnings, ...(ctx.warnings ?? [])]

    return {
      result: ctx.output,
      error: ctx.error,
      fuelUsed: startFuel - ctx.fuel.current,
      trace: ctx.trace,
      warnings: allWarnings.length > 0 ? allWarnings : undefined,
    }
  }
}
